import { createContext, useContext, useState, useEffect } from 'react';
import Papa from 'papaparse';
import { languageCodes } from '../utils/langUtils';

// ---------------- Context Definition ----------------

// Create the language context with sensible defaults so consuming components
// don't break if rendered before translations are loaded.
export const languageContext = createContext({
    language: 'en',              // Currently selected language code
    changeLanguage: () => {},    // Function to switch language
    translation: (key) => key,   // Fallback translation function: just return key
    loading: true,               // Indicates translations are still loading
});

// ---------------- Provider Component ----------------

/**
 * LanguageProvider is a global context provider responsible for:
 * - Fetching a remote `translations.csv` file.
 * - Parsing CSV into a {langCode: { key: value }} map.
 * - Exposing current language, a translation lookup function,
 *   and a setter for switching languages.
 * - Supporting RTL languages (like Arabic) via Unicode control characters.
 * - Handling async/load state so UI can fallback gracefully until ready.
 */
export default function LanguageProvider({ children }) {
    console.log('------------------LanguageProvider rendered----------------------');

    // ---------------- State Hooks ----------------

    const [language, setLanguage] = useState('en');  // Current language; defaults to English
    const [translations, setTranslations] = useState({}); // Parsed translation dictionary
    const [loading, setLoading] = useState(true);    // Tracks fetch/parse completion

    // ---------------- Side Effect: Load Translations on Mount ----------------
    useEffect(() => {
        const loadTranslations = async () => {
            try {
                // Fetch the raw CSV file from AWS S3 bucket
                const response = await fetch('https://mesight.s3.us-east-2.amazonaws.com/translations.csv');
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Convert response to text, useful for CSV parser
                const csvText = await response.text();
                console.log('CSV loaded successfully, first 200 chars:', csvText.substring(0, 200));

                // Parse CSV using Papa Parse library
                Papa.parse(csvText, {
                    header: true,            // First row contains headers
                    skipEmptyLines: true,    // Ignore blank lines
                    transformHeader: (header) => header.trim(), // Clean header whitespaces

                    // ---------------- When Parsing Completes ----------------
                    complete: (result) => {
                        console.log('Papa Parse result:', result);

                        // Log parser errors, but still try to proceed if possible
                        if (result.errors.length > 0) {
                            console.warn('Papa Parse errors:', result.errors);
                        }

                        const translationData = {}; // Will hold the nested { langCode: {key: value} }
                        
                        // The first row reveals which languages exist (columns other than "Key")
                        const firstRow = result.data[0];
                        if (!firstRow) {
                            console.error('No data found in CSV');
                            setTranslations({});
                            setLoading(false); // Even on error, end loading state
                            return;
                        }

                        // Example: ['Key', 'English', 'Français', 'Español', ...]
                        const languages = Object.keys(firstRow).filter(
                            (key) => key.toLowerCase() !== 'key' && key.trim() !== ''
                        );
                        console.log('Found languages:', languages);

                        // Map human-readable headers to ISO-like codes
                        const getLanguageCode = (columnName) => {
                            const lowerColumnName = columnName.toLowerCase();
                            return (
                                languageCodes[lowerColumnName] ||
                                languageCodes[columnName] || // Try exact match if case‑sensitive
                                lowerColumnName // Fallback: use header lowercased
                            );
                        };

                        // Pre-initialize each language bucket
                        languages.forEach((lang) => {
                            const langCode = getLanguageCode(lang);
                            translationData[langCode] = {};
                        });

                        // Populate translations by iterating over each CSV row
                        result.data.forEach((row, index) => {
                            const key = row.Key || row.key; // Translation key is case‑insensitive
                            if (!key || key.trim() === '') {
                                console.log(`Skipping row ${index} - no key found`);
                                return; // Skip malformed rows
                            }

                            // For each language column, add the key/value mapping
                            languages.forEach((lang) => {
                                const translationValue = row[lang];
                                if (translationValue && translationValue.trim() !== '') {
                                    const langCode = getLanguageCode(lang);
                                    // Special case: Arabic is right‑to‑left
                                    if (langCode === 'ar') {
                                        // Wrap with Unicode RLE (Right-to-Left Embedding) and PDF (Pop Directional Formatting)
                                        translationData[langCode][key] = `\u202B${translationValue}\u202C`;
                                    } else {
                                        translationData[langCode][key] = translationValue;
                                    }
                                }
                            });
                        });

                        console.log('Final translation data:', translationData);
                        setTranslations(translationData); // Save parsed translations to state
                        setLoading(false); // Mark as loaded so UI can render
                    },

                    // ---------------- Error Handler for Parsing ----------------
                    error: (error) => {
                        console.error('Error parsing CSV:', error);
                        setTranslations({});
                        setLoading(false); // Prevent infinite "loading" UI
                    },
                });
            } catch (error) {
                // Handle networking/HTTP issues
                console.error('Error fetching CSV:', error);
                setTranslations({});
                setLoading(false);
            }
        };

        // Trigger the async loading once on mount
        loadTranslations();
    }, []);

    // ---------------- Public API Functions ----------------

    /**
     * changeLanguage: switch the current UI language code.
     * All components using `translation()` will re‑render in the new language
     * because `language` state changes.
     */
    const changeLanguage = (newLanguage) => {
        console.log(`Changing language to: ${newLanguage}`);
        setLanguage(newLanguage);
    };

    /**
     * translation: lookup function for translated strings.
     * - If still loading: return the key as fallback.
     * - If no translation found: log diagnostic info and return the key.
     * - Otherwise: return the translated string.
     */
    const translation = (key) => {
        if (loading) {
            console.log(`Translations still loading for key: "${key}" in language: "${language}"`);
            return key; // Don't break UI during load
        }

        const translatedValue = translations[language]?.[key];
        if (!translatedValue) {
            console.log(`Translation not found for key: "${key}" in language: "${language}"`);
            console.log(`Available keys for ${language}:`, Object.keys(translations[language] || {}));
            return key; // Fail gracefully
        }

        return translatedValue;
    };

    // ---------------- Render Provider ----------------
    return (
        <languageContext.Provider
            value={{
                language,                // current language code (state)
                setLanguage,             // direct setter (rarely used; prefer changeLanguage)
                changeLanguage,          // helper method with logging
                translation,              // key -> value lookup function
                loading,                  // expose loading state so UI can display spinners/fallback text
                availableLanguages: Object.keys(translations), // show dropdown of loaded languages
            }}
        >
            {children}
        </languageContext.Provider>
    );
}

// ---------------- Hook to Consume Language Context ----------------

/**
 * useLanguage: convenience hook to access LanguageProvider's values.
 * Throws an error if called outside a <LanguageProvider> tree
 * to prevent accidental undefined access.
 */
export const useLanguage = () => {
    const context = useContext(languageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};